import DateInput from "@/components/inputs/DateInput";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import { ManagementZones } from "@/types/managementZones";
import { HarvestOperation } from "@/types/operations";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { array, date, InferType, mixed, number, object, string } from "yup";
import {
    Button,
    Divider,
    getKeyValue,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
} from "@heroui/react";
import ShapefileUpload from "./ShapefileUpload";
import { FileProperty } from "@/types/file";
import { fileInputSchema, skipTableValidationField } from "@/lib/form/operation";
import TextInput from "@/components/inputs/TextInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faFill,
    faSave,
    faPlusCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import TextAreaInput from "@/components/inputs/TextAreaInput";
import {
    hasFileSource,
    hasInputRates,
    inputRatesToHarvestTableRows,
} from "@/lib/utils/operations";

const schema = object().shape({
    _skipTableValidation: skipTableValidationField,
    start_date: date().required().label("Start Date"),
    note: string().nullable(),
    table: array().when("_skipTableValidation", {
        is: (skip: boolean) => !!skip,
        then: (schema) => schema.notRequired().nullable(),
        otherwise: () =>
            array().of(
                object().shape({
                    zone: number().nullable().label("Zone"),
                    zone_id: number().nullable().label("Zone"),
                    dry_yield: number()
                        .typeError("Required")
                        .required("Required")
                        .min(0)
                        .label("Depth"),
                    moisture: number()
                        .typeError("Not a valid number")
                        .label("Seeding Rate")
                        .min(0),
                }),
            ),
    }),
    batch_fill: mixed(),
    properties: fileInputSchema,
});

type HarvestOperationFormType = InferType<typeof schema>;

function HarvestOperationForm({
    existingHarvestOperationId,
}: {
    existingHarvestOperationId?: number;
}) {
    const { formatUnit } = useI18n();
    const { t } = useTranslation(["operations", "messages"]);
    const COLUMNS = [
        { key: "zone", label: t("zone") },
        {
            key: "dry_yield",
            label: `${t("yield")} (${formatUnit("bushels_per_acre", "kg_ha")})`,
        },
        { key: "moisture", label: t("moisture") + " (%)" },
    ];
    const { fieldId } = useFieldDashboard() ?? {};
    const { useGetRequest, useMutateRequest } = useRequest();
    const navigate = useNavigate();

    const methods = useForm<HarvestOperationFormType>({
        resolver: yupResolver(schema),
        defaultValues: {
            _skipTableValidation: false,
        },
    });

    const managementZonesQuery = useGetRequest<ManagementZones[]>({
        url: `fields/${fieldId}/management_zones/`,
    });

    const existingHarvestOperationQuery = useGetRequest<HarvestOperation>({
        url: `harvest_operations/${existingHarvestOperationId}/`,
        enabled: !!existingHarvestOperationId,
    });

    const hasStoredInputRates = hasInputRates(
        existingHarvestOperationQuery.data?.input_rates,
    );

    const existingHarvestOperationRatesQuery = useGetRequest<{
        id: number;
        zone_number: number;
        mean_1: number;
        mean_2: number;
    }>({
        url: `harvest_operations/${existingHarvestOperationId}/rates/`,
        params: {
            by: "zone",
        },
        enabled: !!existingHarvestOperationId && !hasStoredInputRates,
    });

    const preferredManagementZones = useMemo(() => {
        return managementZonesQuery.data?.find((zone) => zone.is_primary);
    }, [managementZonesQuery.data]);

    const fileProperties = useGetRequest<FileProperty[]>({
        url: `harvest_operations/file_properties/`,
    });

    const [selectedFile, setSelectedFile] = useState<File | undefined | null>(
        undefined,
    );

    const { handleSubmit, setValue, getValues, trigger, watch } = methods;

    const [batchFill] = watch(["batch_fill"]);

    useEffect(() => {
        if (existingHarvestOperationQuery.data) {
            setValue(
                "start_date",
                dayjs(existingHarvestOperationQuery.data.start_date).toDate(),
            );
            setValue("note", existingHarvestOperationQuery.data.note);
        }
    }, [existingHarvestOperationQuery.data, setValue]);

    const saveHarvestOperationMutation = useMutateRequest<{ id: number }>({
        successMessage: t("success.harvest_operation_saved"),
        errorMessage: t("error.failed_to_save_harvest_operation"),
    });

    const uploadOperation = useMutateRequest({
        errorMessage: t("error.failed_to_upload_operation"),
        successMessage: t("success.operation_uploaded_successfully"),
    });

    const onSubmit = (formData: HarvestOperationFormType) => {
        const payload = {
            start_date: dayjs(formData.start_date).format("YYYY-MM-DD"),
            field: fieldId,
            note: formData.note === "" ? null : formData.note,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any;

        const rates =
            formData.table?.map((row) => ({
                zone: row.zone_id,
                rate_1: row?.dry_yield,
                rate_2: row?.moisture,
            })) ?? [];

        if (!selectedFile && !hasFileSource(existingHarvestOperationQuery.data)) {
            if (preferredManagementZones) {
                payload["zone_rates"] = rates;
            } else {
                payload["field_rate"] = rates[0];
            }
        }
        saveHarvestOperationMutation.mutate(
            {
                url: existingHarvestOperationId
                    ? `harvest_operations/${existingHarvestOperationId}/`
                    : `harvest_operations/`,
                method: existingHarvestOperationId ? "PUT" : "POST",
                data: payload,
            },
            {
                onSuccess: (data) => {
                    if (selectedFile) {
                        const fileUpload = new FormData();
                        fileUpload.append("file", selectedFile);
                        fileUpload.append(
                            "properties",
                            JSON.stringify(getValues("properties")),
                        );
                        uploadOperation.mutate(
                            {
                                data: fileUpload,
                                url: `harvest_operations/${data.id}/uploadV2/`,
                                method: "put",
                            },
                            {
                                onSuccess: () => {
                                    navigate({
                                        to: "/dashboard/fields/$fieldId/operations",
                                        params: {
                                            fieldId: fieldId as string,
                                        },
                                    });
                                },
                            },
                        );
                    } else {
                        navigate({
                            to: "/dashboard/fields/$fieldId/operations",
                            params: {
                                fieldId: fieldId as string,
                            },
                        });
                    }
                },
            },
        );
    };

    const { fields } = useFieldArray<HarvestOperationFormType, "table">({
        control: methods.control,
        name: "table",
    });

    useEffect(() => {
        if (
            !existingHarvestOperationId ||
            !existingHarvestOperationQuery.data
        ) {
            return;
        }

        const inputRates = existingHarvestOperationQuery.data.input_rates;
        if (hasInputRates(inputRates)) {
            const rows = inputRatesToHarvestTableRows(inputRates!).map(
                (row) => ({
                    ...row,
                    zone:
                        row.zone_id && preferredManagementZones
                            ? (preferredManagementZones.zones.find(
                                (zone) => zone.id === row.zone_id,
                            )?.zone_number ?? null)
                            : row.zone,
                }),
            );
            setValue("table", rows);
            return;
        }

        if (!existingHarvestOperationRatesQuery.data) {
            return;
        }

        if (Array.isArray(existingHarvestOperationRatesQuery.data)) {
            setValue(
                "table",
                (
                    existingHarvestOperationRatesQuery.data as unknown as {
                        [x: string]: number;
                    }[]
                )?.map((rate) => ({
                    zone: rate.zone_number,
                    moisture: rate.mean_2,
                    dry_yield: rate.mean_1 ?? rate.mean,
                    zone_id: rate.id,
                })),
            );
        } else if (existingHarvestOperationQuery.data.irrigated_averages) {
            setValue("table", [
                {
                    zone: null,
                    moisture:
                        existingHarvestOperationQuery.data.irrigated_averages
                            ?.moisture,
                    dry_yield:
                        existingHarvestOperationQuery.data.irrigated_averages
                            ?.yield,
                    zone_id: null,
                },
            ]);
        } else {
            setValue("table", [
                {
                    zone: null,
                    moisture:
                        existingHarvestOperationQuery.data.field_averages
                            ?.moisture,
                    dry_yield:
                        existingHarvestOperationQuery.data.field_averages
                            ?.yield,
                    zone_id: null,
                },
            ]);
        }
    }, [
        existingHarvestOperationId,
        existingHarvestOperationQuery.data,
        existingHarvestOperationRatesQuery.data,
        preferredManagementZones,
        setValue,
    ]);

    useEffect(() => {
        if (!existingHarvestOperationId) {
            if (preferredManagementZones) {
                setValue(
                    "table",
                    preferredManagementZones.zones.map((zone) => ({
                        zone: zone.zone_number,
                        moisture: 0,
                        dry_yield: 0,
                        zone_id: zone.id,
                    })),
                );
            } else {
                setValue("table", [
                    {
                        zone: null,
                        moisture: 0,
                        dry_yield: 0,
                        zone_id: null,
                    },
                ]);
            }
        }
    }, [
        existingHarvestOperationId,
        existingHarvestOperationRatesQuery.data,
        preferredManagementZones,
        setValue,
    ]);

    const handleBatchFill = useCallback(
        (key: "dry_yield" | "moisture") => {
            const formTable = getValues("table");
            setValue(
                "table",
                formTable?.map((row) => {
                    row[key] = batchFill[key];
                    return row;
                }),
            );
        },
        [batchFill, getValues, setValue],
    );

    const wasFileUploadOrOperation = useMemo(
        () => hasFileSource(existingHarvestOperationQuery.data),
        [existingHarvestOperationQuery.data],
    );

    const tableReadOnly =
        !!existingHarvestOperationId && wasFileUploadOrOperation;

    useEffect(() => {
        setValue(
            "_skipTableValidation",
            wasFileUploadOrOperation || !!selectedFile,
        );
    }, [wasFileUploadOrOperation, selectedFile, setValue]);

    return (
        <FormProvider {...methods}>
            <form>
                <div className="flex flex-col gap-2">
                    <DateInput
                        name="start_date"
                        label={t("date")}
                        variant="flat"
                        isRequired
                        className="w-full"
                        size="sm"
                        labelPlacement="outside"
                        showMonthAndYearPickers
                    />
                    <TextAreaInput
                        name="note"
                        label={t("note")}
                        variant="flat"
                        size="sm"
                        labelPlacement="outside"
                        placeholder={t("note")}
                    />
                    <Divider />
                    {!wasFileUploadOrOperation && (
                        <ShapefileUpload
                            fileInputProperties={fileProperties.data ?? []}
                            setFile={setSelectedFile}
                        />
                    )}

                    {wasFileUploadOrOperation && (
                        <div className="text-red-500 text-sm italic">
                            This operation was uploaded or synced from a
                            connection
                        </div>
                    )}
                    {!selectedFile && (
                        <>
                            <div className="font-semibold text-lg flex flex-row justify-between items-center">
                                <div className="font-semibold text-lg">
                                    {t("harvest") + " " + t("table")}
                                </div>
                                {!tableReadOnly && (
                                    <div className="flex flex-row gap-2">
                                        <TextInput
                                            name="batch_fill.dry_yield"
                                            size="sm"
                                            label={
                                                t("yield") +
                                                " (" +
                                                formatUnit(
                                                    "bushels_per_acre",
                                                    "kg_ha",
                                                ) +
                                                ")"
                                            }
                                            labelPlacement="outside"
                                            type="number"
                                            classNames={{
                                                inputWrapper: "pr-0",
                                            }}
                                            placeholder="0"
                                            isDisabled={tableReadOnly}
                                            endContent={
                                                <Button
                                                    isIconOnly
                                                    as={"div"}
                                                    size="sm"
                                                    color="primary"
                                                    isDisabled={tableReadOnly}
                                                    onPress={() =>
                                                        handleBatchFill("dry_yield")
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faFill}
                                                    />
                                                </Button>
                                            }
                                        />
                                        <TextInput
                                            name="batch_fill.moisture"
                                            size="sm"
                                            label={t("moisture") + " (%)"}
                                            labelPlacement="outside"
                                            type="number"
                                            classNames={{
                                                inputWrapper: "pr-0",
                                            }}
                                            placeholder="0"
                                            isDisabled={tableReadOnly}
                                            endContent={
                                                <Button
                                                    isIconOnly
                                                    as={"div"}
                                                    size="sm"
                                                    color="primary"
                                                    isDisabled={tableReadOnly}
                                                    onPress={() =>
                                                        handleBatchFill("moisture")
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faFill}
                                                    />
                                                </Button>
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                            <Table
                                aria-label="Application Table"
                                classNames={{
                                    td: "p-1",
                                }}
                            >
                                <TableHeader columns={COLUMNS}>
                                    {(item) => (
                                        <TableColumn key={item.key}>
                                            {item.label}
                                        </TableColumn>
                                    )}
                                </TableHeader>
                                <TableBody
                                    isLoading={
                                        existingHarvestOperationQuery.isFetching ||
                                        existingHarvestOperationRatesQuery.isFetching ||
                                        managementZonesQuery.isFetching
                                    }
                                    loadingContent={
                                        <Spinner label="Loading..." />
                                    }
                                    emptyContent={
                                        <div className="text-red-500 italic">
                                            No table found, please contact
                                            support
                                        </div>
                                    }
                                >
                                    {fields.map((item, index) => (
                                        <TableRow key={item.id}>
                                            {(columnKey) => {
                                                if (columnKey === "dry_yield") {
                                                    return (
                                                        <TableCell>
                                                            <TextInput
                                                                isDisabled={
                                                                    tableReadOnly
                                                                }
                                                                key={item.id}
                                                                aria-label="Dry Yield"
                                                                size="sm"
                                                                type="number"
                                                                name={`table.${index}.dry_yield`}
                                                                placeholder="Dry Yield"
                                                                decimalPlaces={
                                                                    2
                                                                }
                                                                hideErrorMessage
                                                            />
                                                        </TableCell>
                                                    );
                                                }
                                                if (columnKey === "moisture") {
                                                    return (
                                                        <TableCell>
                                                            <TextInput
                                                                isDisabled={
                                                                    tableReadOnly
                                                                }
                                                                key={item.id}
                                                                aria-label="Moisture"
                                                                size="sm"
                                                                type="number"
                                                                name={`table.${index}.moisture`}
                                                                placeholder="Moisture"
                                                                decimalPlaces={
                                                                    2
                                                                }
                                                                hideErrorMessage
                                                            />
                                                        </TableCell>
                                                    );
                                                }
                                                return (
                                                    <TableCell>
                                                        {getKeyValue(
                                                            item,
                                                            columnKey,
                                                        )}
                                                    </TableCell>
                                                );
                                            }}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </>
                    )}
                    <Button
                        fullWidth
                        color="secondary"
                        onPress={() => {
                            trigger();
                            handleSubmit(onSubmit)();
                        }}
                        isLoading={
                            saveHarvestOperationMutation.isPending ||
                            uploadOperation.isPending
                        }
                    >
                        <FontAwesomeIcon
                            icon={
                                existingHarvestOperationId
                                    ? faSave
                                    : faPlusCircle
                            }
                            className="mr-2"
                        />
                        {existingHarvestOperationId
                            ? t("saveOperation")
                            : t("addOperation")}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
}

export default HarvestOperationForm;
